import { useState, useEffect, useCallback } from 'react';
import {
  KeyRound, Plus, Trash2, Check, X, Copy, AlertTriangle, Layers, Clock, ShieldOff, Loader2, Zap,
} from 'lucide-react';

/**
 * Platform Admin "Org Licenses" tab.
 *
 * Layered ABOVE the existing tenant-scoped CCM Licenses tab — both coexist.
 * Manages JWT-signed organization-level license keys: list, generate, activate, revoke.
 */

const STATUS_COLORS = {
  ACTIVE:    { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' },
  EXPIRED:   { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
  REVOKED:   { bg: 'rgba(156, 163, 175, 0.15)', color: '#9ca3af' },
  SUSPENDED: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
  INVALID:   { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
};

const TIER_OPTIONS = [
  { value: 'PARTNER',    label: 'Partner' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'SITE',       label: 'Site' },
];

const MODULE_LIST = [
  { key: 'cameras',             label: 'Cameras',             hasLimit: true  },
  { key: 'access_control',      label: 'Access Control',      hasLimit: true  },
  { key: 'intercoms',           label: 'Intercoms',           hasLimit: true  },
  { key: 'monitoring',          label: 'Monitoring',          hasLimit: true  },
  { key: 'recording_assurance', label: 'Recording Assurance', hasLimit: true  },
  { key: 'entry',               label: 'Entry',               hasLimit: true  },
  { key: 'reports',             label: 'Reports',             hasLimit: false },
];

const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

const initialModules = () => Object.fromEntries(MODULE_LIST.map(m => [m.key, { enabled: true, limit: m.hasLimit ? 10 : 0 }]));

const OrgLicensesTab = ({ organizations }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [error, setError] = useState(null);

  const fetchLicenses = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/org-licenses', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLicenses(data.licenses || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      const res = await fetch(`/api/platform/org-licenses/${revokeTarget.organizationId}/revoke`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRevokeTarget(null);
      fetchLicenses();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      <div className="platform-toolbar">
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
          Org-level umbrella licenses. Layered above tenant CCM licenses — both coexist.
        </p>
        <button className="btn btn-primary" onClick={() => setShowGenerateModal(true)}>
          <Plus size={16} /> Generate License
        </button>
      </div>

      {error && (
        <div className="md-license-banner expired" style={{ marginBottom: 12 }}>
          <div className="md-license-banner-content">
            <AlertTriangle size={16} /> {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="platform-loading"><div className="platform-spinner" /></div>
      ) : licenses.length === 0 ? (
        <div className="platform-empty">
          <div className="platform-empty-icon"><KeyRound size={32} /></div>
          <h3>No org licenses yet</h3>
          <p>Generate a license key and activate it on an organization to get started.</p>
        </div>
      ) : (
        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Licensee</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Tenants</th>
                <th>Modules</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {licenses.map(lic => {
                const sc = STATUS_COLORS[lic.status] || STATUS_COLORS.ACTIVE;
                const moduleSummary = MODULE_LIST.filter(m => lic[m.key.replace(/_(\w)/g, (_, c) => c.toUpperCase()) + 'Enabled']).length;
                return (
                  <tr key={lic.id}>
                    <td><strong>{lic.organization?.name || lic.organizationId}</strong></td>
                    <td>{lic.licensee}</td>
                    <td>{lic.tier}</td>
                    <td>
                      <span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                        {lic.status}
                      </span>
                    </td>
                    <td>{formatDate(lic.expiresAt)}</td>
                    <td>{lic.maxTenants}</td>
                    <td>{moduleSummary} of {MODULE_LIST.length}</td>
                    <td>
                      <button className="btn-icon-sm danger" onClick={() => setRevokeTarget(lic)} title="Revoke">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showGenerateModal && (
        <GenerateLicenseModal
          organizations={organizations || []}
          onClose={() => setShowGenerateModal(false)}
          onActivated={() => { setShowGenerateModal(false); fetchLicenses(); }}
        />
      )}

      {revokeTarget && (
        <div className="platform-modal-overlay" onClick={() => setRevokeTarget(null)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} /> Revoke License</h3>
              <button onClick={() => setRevokeTarget(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <p>Revoke the org license for <strong>{revokeTarget.organization?.name}</strong>?</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                The org will fall back to tenant-level licensing only.
              </p>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setRevokeTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRevoke}>Revoke</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GenerateLicenseModal = ({ organizations, onClose, onActivated }) => {
  const [form, setForm] = useState({
    orgId: '*',
    licensee: '',
    tier: 'PARTNER',
    maxTenants: 10,
    expiresInDays: 365,
    whiteLabel: false,
    offlineValid: false,
    modules: initialModules(),
  });
  const [generating, setGenerating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const updateModule = (key, patch) => {
    setForm(p => ({ ...p, modules: { ...p.modules, [key]: { ...p.modules[key], ...patch } } }));
  };

  const handleGenerate = async () => {
    setError(null);
    if (!form.licensee.trim()) { setError('Licensee name required'); return; }
    setGenerating(true);
    try {
      const res = await fetch('/api/platform/org-licenses/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setGeneratedKey(data.key);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleActivate = async () => {
    if (!generatedKey) return;
    if (form.orgId === '*') {
      setError('Cannot auto-activate a wildcard key. Pick a specific org or paste the key into the org admin settings.');
      return;
    }
    setActivating(true);
    setError(null);
    try {
      const res = await fetch(`/api/platform/org-licenses/${form.orgId}/activate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: generatedKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onActivated();
    } catch (e) {
      setError(e.message);
    } finally {
      setActivating(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal org-license-modal" onClick={e => e.stopPropagation()}>
        <div className="platform-modal-header">
          <h3><KeyRound size={18} /> Generate Org License</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="platform-modal-body">
          {error && (
            <div className="org-license-error">
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          {!generatedKey && (
            <>
              <div className="platform-form-group">
                <label>Organization</label>
                <select value={form.orgId} onChange={e => setForm(p => ({ ...p, orgId: e.target.value }))}>
                  <option value="*">— Wildcard (any org) —</option>
                  {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="platform-form-group">
                <label>Licensee Name</label>
                <input
                  type="text"
                  value={form.licensee}
                  onChange={e => setForm(p => ({ ...p, licensee: e.target.value }))}
                  placeholder="Partner Security LLC"
                />
              </div>

              <div className="platform-form-group">
                <label>Tier</label>
                <select value={form.tier} onChange={e => setForm(p => ({ ...p, tier: e.target.value }))}>
                  {TIER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="platform-form-group">
                <label>Modules</label>
                <div className="org-license-modules">
                  {MODULE_LIST.map(m => {
                    const cfg = form.modules[m.key];
                    return (
                      <div key={m.key} className="org-license-module-row">
                        <label className="org-license-module-toggle">
                          <input
                            type="checkbox"
                            checked={cfg.enabled}
                            onChange={e => updateModule(m.key, { enabled: e.target.checked })}
                          />
                          <span>{m.label}</span>
                        </label>
                        {m.hasLimit && cfg.enabled && (
                          <input
                            type="number"
                            min="-1"
                            value={cfg.limit}
                            onChange={e => updateModule(m.key, { limit: parseInt(e.target.value, 10) || 0 })}
                            className="org-license-limit-input"
                            title="-1 = unlimited"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="platform-form-hint">Use <code>-1</code> for unlimited.</p>
              </div>

              <div className="platform-form-group">
                <label>Max Tenants</label>
                <input
                  type="number"
                  min="1"
                  value={form.maxTenants}
                  onChange={e => setForm(p => ({ ...p, maxTenants: parseInt(e.target.value, 10) || 1 }))}
                />
              </div>

              <div className="platform-form-group">
                <label>Expires In (days)</label>
                <input
                  type="number"
                  min="1"
                  value={form.expiresInDays}
                  onChange={e => setForm(p => ({ ...p, expiresInDays: parseInt(e.target.value, 10) || 1 }))}
                />
              </div>

              <div className="platform-form-group">
                <label className="org-license-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.whiteLabel}
                    onChange={e => setForm(p => ({ ...p, whiteLabel: e.target.checked }))}
                  />
                  <span>White-label enabled</span>
                </label>
                <label className="org-license-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.offlineValid}
                    onChange={e => setForm(p => ({ ...p, offlineValid: e.target.checked }))}
                  />
                  <span>Valid in air-gapped (offline) deployments</span>
                </label>
              </div>
            </>
          )}

          {generatedKey && (
            <>
              <div className="org-license-key-success">
                <Check size={18} /> License key generated.
              </div>
              <div className="platform-form-group">
                <label>License Key</label>
                <textarea
                  value={generatedKey}
                  readOnly
                  rows={6}
                  className="org-license-key-display"
                  onClick={e => e.target.select()}
                />
                <button className="btn btn-secondary btn-small" onClick={copyKey} style={{ marginTop: 8 }}>
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Key</>}
                </button>
              </div>
              <p className="platform-form-hint">
                {form.orgId === '*'
                  ? 'Wildcard key — paste into the org admin settings on each org you want to activate.'
                  : 'Click "Activate on Org" to apply this key now.'}
              </p>
            </>
          )}
        </div>

        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {!generatedKey ? (
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating ? <><Loader2 size={14} className="spin" /> Generating...</> : <><Zap size={14} /> Generate</>}
            </button>
          ) : form.orgId !== '*' ? (
            <button className="btn btn-primary" onClick={handleActivate} disabled={activating}>
              {activating ? <><Loader2 size={14} className="spin" /> Activating...</> : <><Check size={14} /> Activate on Org</>}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OrgLicensesTab;
